import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { FaCreditCard, FaLock, FaTrash } from 'react-icons/fa';
import apiClient from '../../apiClient';
import './PaymentMethodsEmbedded.css';

// Payment form component
function PaymentMethodForm({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Confirm the setup to save the payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
        return;
      }

      // Payment method saved successfully
      if (onSuccess) {
        onSuccess(setupIntent.payment_method);
      }
    } catch (error) {
      console.error('Error saving payment method:', error);
      setError('Failed to save payment method. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-method-form">
      <div className="payment-element-wrapper">
        <PaymentElement 
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
            business: { name: 'ezOCM' }
          }}
        />
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="form-actions">
        <button 
          type="button"
          className="cancel-button" 
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
        <button 
          type="submit"
          className="save-button" 
          disabled={!stripe || processing}
        >
          {processing ? 'Saving...' : 'Save Payment Method'}
        </button>
      </div>
    </form>
  );
}

// Main component
export default function PaymentMethodsEmbedded({ companyId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    initializeStripe();
    fetchPaymentMethods();
  }, []);

  const initializeStripe = async () => {
    try {
      const response = await apiClient.get('/stripe/config');
      if (response.data.publishable_key) {
        const stripe = loadStripe(response.data.publishable_key);
        setStripePromise(stripe);
      }
    } catch (error) {
      console.error('Failed to load Stripe config:', error);
      setError('Payment system unavailable');
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/stripe/payment-methods');
      console.log('Payment methods response:', response.data);
      
      // Also fetch debug info
      try {
        const debugResponse = await apiClient.get('/stripe/debug-payment-methods');
        console.log('Debug payment methods:', debugResponse.data);
      } catch (debugError) {
        console.error('Debug endpoint error:', debugError);
      }
      
      setPaymentMethods(response.data.payment_methods || []);
      
      if (response.data.error) {
        console.error('Payment methods error:', response.data.error);
        setError(response.data.error);
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      // Create a SetupIntent for adding a new payment method
      const response = await apiClient.post('/stripe/create-setup-intent');
      if (response.data.client_secret) {
        setClientSecret(response.data.client_secret);
        setShowAddForm(true);
      }
    } catch (error) {
      console.error('Failed to create setup intent:', error);
      setError('Failed to initialize payment form. Please try again.');
    }
  };

  const handlePaymentMethodAdded = async (paymentMethodId) => {
    try {
      // Attach the payment method to the customer
      await apiClient.post('/stripe/attach-payment-method', {
        payment_method_id: paymentMethodId
      });
      
      // Refresh the list
      await fetchPaymentMethods();
      setShowAddForm(false);
      setClientSecret(null);
    } catch (error) {
      console.error('Failed to attach payment method:', error);
      setError('Failed to save payment method. Please try again.');
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await apiClient.post('/stripe/set-default-payment-method', {
        payment_method_id: paymentMethodId
      });
      await fetchPaymentMethods();
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      setError('Failed to update default payment method.');
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      await apiClient.post('/stripe/remove-payment-method', {
        payment_method_id: paymentMethodId
      });
      await fetchPaymentMethods();
    } catch (error) {
      console.error('Failed to remove payment method:', error);
      setError('Failed to remove payment method.');
    }
  };

  const formatCardBrand = (brand) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  if (loading) {
    return <div className="loading">Loading payment methods...</div>;
  }

  return (
    <div className="payment-methods-embedded">
      <div className="payment-methods-header">
        <h4>Payment Methods</h4>
        <p className="security-note">
          <FaLock /> Your payment information is securely stored by Stripe
        </p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {!showAddForm ? (
        <>
          <div className="payment-methods-list">
            {paymentMethods.length === 0 ? (
              <div className="no-payment-methods">
                <FaCreditCard className="empty-icon" />
                <p>No payment methods on file</p>
                <button 
                  className="add-payment-button primary"
                  onClick={handleAddPaymentMethod}
                >
                  Add Payment Method
                </button>
              </div>
            ) : (
              <>
                {paymentMethods.map((method) => (
                  <div key={method.id} className="payment-method-item">
                    <div className="payment-method-info">
                      <FaCreditCard className="card-icon" />
                      <div className="card-details">
                        <span className="card-brand">{formatCardBrand(method.card.brand)}</span>
                        <span className="card-last4">•••• {method.card.last4}</span>
                        <span className="card-expiry">
                          Expires {method.card.exp_month}/{method.card.exp_year}
                        </span>
                        {method.is_default && (
                          <span className="default-badge">Default</span>
                        )}
                      </div>
                    </div>
                    <div className="payment-method-actions">
                      {!method.is_default && (
                        <button
                          className="action-button"
                          onClick={() => handleSetDefault(method.id)}
                          title="Set as default"
                        >
                          Make Default
                        </button>
                      )}
                      <button
                        className="action-button remove"
                        onClick={() => handleRemovePaymentMethod(method.id)}
                        title="Remove payment method"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
                <button 
                  className="add-payment-button"
                  onClick={handleAddPaymentMethod}
                >
                  Add Another Payment Method
                </button>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="add-payment-form">
          <h5>Add Payment Method</h5>
          {stripePromise && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentMethodForm 
                onSuccess={handlePaymentMethodAdded}
                onCancel={() => {
                  setShowAddForm(false);
                  setClientSecret(null);
                }}
              />
            </Elements>
          )}
        </div>
      )}
    </div>
  );
}